import { useEffect, useState } from 'react'
import {
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui'
import { useContractGroups, useCreateProduct, useUpdateProduct } from '../api/contractsApi.ts'
import type { Product, ProductCategory, ProductSource } from '../types.ts'
import { SapPartnerPicker } from './SapPartnerPicker.tsx'
import { SapArticlePicker } from './SapArticlePicker.tsx'

interface ProductFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  product?: Product | null
  onSaved?: (product: Product) => void
}

interface FormState {
  name: string
  sku: string
  description: string
  category: ProductCategory
  unit: string
  defaultSource: ProductSource
  defaultSupplierSapCardCode: string
  sapItemCode: string
  sapItemName: string
  alsoSku: string
  esetProductId: string
  m365LicenseSku: string
  contractGroupId: string
  setupFeeEur: string
  active: boolean
}

const EMPTY: FormState = {
  name: '',
  sku: '',
  description: '',
  category: 'service',
  unit: '',
  defaultSource: 'manual',
  defaultSupplierSapCardCode: '',
  sapItemCode: '',
  sapItemName: '',
  alsoSku: '',
  esetProductId: '',
  m365LicenseSku: '',
  contractGroupId: '',
  setupFeeEur: '',
  active: true,
}

const CATEGORY_OPTIONS: { value: ProductCategory; label: string }[] = [
  { value: 'service', label: 'Service / Vertragstyp' },
  { value: 'license', label: 'Lizenz' },
  { value: 'device', label: 'Gerät / Asset' },
  { value: 'support', label: 'Support / Stunden' },
  { value: 'storage', label: 'Storage' },
  { value: 'other', label: 'Sonstiges' },
]

const SOURCE_OPTIONS: { value: ProductSource; label: string }[] = [
  { value: 'manual', label: 'Manuell' },
  { value: 'also', label: 'ALSO' },
  { value: 'eset', label: 'ESET' },
  { value: 'tanss', label: 'TANSS' },
  { value: 'cwa', label: 'CWA' },
  { value: 'm365', label: 'Microsoft 365' },
  { value: 'sap', label: 'SAP' },
]

export function ProductFormModal({ open, onOpenChange, product, onSaved }: ProductFormModalProps) {
  const isEdit = Boolean(product)
  const { toast } = useToast()
  const create = useCreateProduct()
  const update = useUpdateProduct(product?.id ?? '')
  const [form, setForm] = useState<FormState>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const { data: contractGroups = [] } = useContractGroups()

  useEffect(() => {
    if (!open) return
    if (product) {
      setForm({
        name: product.name,
        sku: product.sku ?? '',
        description: product.description ?? '',
        category: product.category,
        unit: product.unit,
        defaultSource: product.defaultSource,
        defaultSupplierSapCardCode: product.defaultSupplierSapCardCode ?? '',
        sapItemCode: product.sapItemCode ?? '',
        // Snapshot-Name kennen wir nur wenn SapArticlePicker zur Anzeige gebraucht wird –
        // wir leben mit „nur Code anzeigen" beim ersten Open, beim ersten Picker-Reload kommt
        // dann der Live-Name. Akzeptabel fuer Etappe 1.5.
        sapItemName: '',
        alsoSku: product.alsoSku ?? '',
        esetProductId: product.esetProductId ?? '',
        m365LicenseSku: product.m365LicenseSku ?? '',
        contractGroupId: product.contractGroupId ?? '',
        setupFeeEur: product.setupFeeCents != null ? String(product.setupFeeCents / 100) : '',
        active: product.active,
      })
    } else {
      setForm(EMPTY)
    }
    setError(null)
  }, [open, product])

  function update_<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleSave() {
    if (!form.name.trim()) {
      setError('Name ist Pflicht.')
      return
    }
    if (!form.unit.trim()) {
      setError('Einheit ist Pflicht (z. B. „Lizenz", „Gerät", „Stunde").')
      return
    }
    setError(null)

    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      description: form.description.trim() || null,
      category: form.category,
      unit: form.unit.trim(),
      defaultSource: form.defaultSource,
      defaultSupplierSapCardCode: form.defaultSupplierSapCardCode.trim() || null,
      sapItemCode: form.sapItemCode.trim() || null,
      alsoSku: form.alsoSku.trim() || null,
      esetProductId: form.esetProductId.trim() || null,
      m365LicenseSku: form.m365LicenseSku.trim() || null,
      contractGroupId: form.contractGroupId || null,
      setupFeeCents: form.setupFeeEur.trim() ? Math.round(parseFloat(form.setupFeeEur) * 100) : null,
      active: form.active,
    }

    const handler = (saved: Product) => {
      toast({ title: isEdit ? 'Produkt aktualisiert' : 'Produkt angelegt', tone: 'success' })
      onSaved?.(saved)
    }
    const onError = (err: Error) => {
      setError(err.message || 'Speichern fehlgeschlagen.')
    }

    if (isEdit && product) {
      update.mutate(payload, { onSuccess: handler, onError })
    } else {
      create.mutate(payload, { onSuccess: handler, onError })
    }
  }

  const isPending = create.isPending || update.isPending

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={isEdit ? 'Produkt bearbeiten' : 'Neues Produkt'}
      description={`Produkte sind Vertragsbausteine: Vertragstypen (Kategorie „Service"), Lizenzen, Geräte oder Support-Stunden.`}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}

        <Input
          label="Name"
          value={form.name}
          onChange={(e) => update_('name', e.currentTarget.value)}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="SKU"
            hint="Optional — z. B. ALSO-/SAP-Artikelnummer"
            value={form.sku}
            onChange={(e) => update_('sku', e.currentTarget.value)}
          />
          <Input
            label="Einheit"
            hint="z. B. Lizenz, Gerät, GB, Stunde"
            value={form.unit}
            onChange={(e) => update_('unit', e.currentTarget.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select<ProductCategory>
            label="Kategorie"
            value={form.category}
            onValueChange={(v) => update_('category', v)}
            options={CATEGORY_OPTIONS}
          />
          <Select<ProductSource>
            label="Standard-Quelle"
            hint="Woher kommt das Ist im späteren Reconciliation-Schritt"
            value={form.defaultSource}
            onValueChange={(v) => update_('defaultSource', v)}
            options={SOURCE_OPTIONS}
          />
        </div>

        <SapPartnerPicker
          label="Standard-Lieferant (SAP)"
          hint="Optional — Geschäftspartner aus SAP für Margenrechnung später"
          value={form.defaultSupplierSapCardCode}
          onChange={(cardCode) => update_('defaultSupplierSapCardCode', cardCode)}
        />

        <SapArticlePicker
          label="SAP-Artikel"
          hint="Brücke zum SAP-Artikelstamm. Wird für Margenrechnung und SAP-Buchung der Abrechnung verwendet."
          value={form.sapItemCode}
          initialName={form.sapItemName}
          onChange={(itemCode, name) => {
            update_('sapItemCode', itemCode)
            if (name) update_('sapItemName', name)
          }}
        />

        {form.category === 'service' && (
          <Select<string>
            label="Vertragsgruppe"
            hint="Optional — bündelt diesen Vertragstyp mit anderen Typen / Verträgen einer Gruppe."
            value={form.contractGroupId}
            onValueChange={(v) => update_('contractGroupId', v)}
            options={[
              { value: '', label: '— keine Gruppe —' },
              ...contractGroups.map((g) => ({ value: g.id, label: g.name })),
            ]}
          />
        )}

        <div className="grid grid-cols-3 gap-3">
          <Input
            label="ALSO SKU"
            hint="für Subscription-Abgleich"
            value={form.alsoSku}
            onChange={(e) => update_('alsoSku', e.currentTarget.value)}
          />
          <Input
            label="ESET Produkt-ID"
            hint="für Lizenz-Abgleich"
            value={form.esetProductId}
            onChange={(e) => update_('esetProductId', e.currentTarget.value)}
          />
          <Input
            label="M365 License-SKU"
            hint="für Microsoft Graph-Abgleich"
            value={form.m365LicenseSku}
            onChange={(e) => update_('m365LicenseSku', e.currentTarget.value)}
          />
        </div>

        <Input
          label="Setup-Pauschale (€)"
          hint="Einmalige Pauschale bei Neuanlage eines Benutzers"
          value={form.setupFeeEur}
          onChange={(e) => update_('setupFeeEur', e.currentTarget.value)}
          type="number"
          min="0"
          step="0.01"
        />

        <Textarea
          label="Beschreibung"
          rows={3}
          value={form.description}
          onChange={(e) => update_('description', e.currentTarget.value)}
        />

        <Switch
          label="Aktiv"
          hint="Inaktive Produkte erscheinen nicht in Vertragsanlage"
          checked={form.active}
          onChange={(e) => update_('active', e.currentTarget.checked)}
        />
      </div>
    </Modal>
  )
}
